import { useEffect, useMemo, useRef, useState, } from "react";
import { colors, fonts, frames, glyphs, } from "../../Styles";
import { NewInput, NewModal } from "..";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types, NewInputHandles } from "../../Definitions";
import { useTranslation } from "react-i18next";
import { useUser } from "../../Services"
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { GenericDataParameters, useColumnConstructor } from "../Tables/_Tables/useColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import { setStates } from "../../Store/StateHandlers/active-slice";
import { Pages, NavigationFunction as RootNavigation } from "../../Navigation/root";
import globalFilter from "../Tables/_Tables/useGlobalFilter";
import NewButton from "../BaseComponents/newButton";
import { clearAnimals } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { clearActiveAnimals } from "../../Store/activeStateHandlers/activeAnimalSlice";
import { clearFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { clearActiveLocations } from "../../Store/activeStateHandlers/activeSectionSlice";

interface siteListProps {
  ifSuccess: (domain: string) => void
}

export default function SiteListModal({ifSuccess}:siteListProps) {
  const {t} = useTranslation()
  const { userFarms, getUserFarms, doLogOut, userToken, getFarmSettings, getAdminInfo, adminInfo } = useUser()
  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const dispatch = useDispatch()
  const navigator = useNavigate()

  localStorage.setItem("activedomain", "")

  useEffect(() => {
    getUserFarms()
    getAdminInfo()
    dispatch(setStates({type: 'farm', farm: ""}));
  }, [])

  useEffect(() => {
    setGlobalFilterValue(searchTerm);
  }, [searchTerm]);
  const tableData = useMemo(() => (
    userFarms?.map((farm) => ({
      action: <NewButton content={farm.name}
        onClick={() => {
          //If the farm is on a legacy system forward to legacy app
          if (farm.legacy) {
            window.location.href = `${window.location.protocol}//${window.location.hostname}` + "/autopig-app" + "?jwt=" + encodeURIComponent(userToken??"");
          }  else {
            getFarmSettings(farm.domain);
            dispatch(setStates({type:'farm', farm:farm.domain}));
            dispatch(clearAnimals());
            dispatch(clearActiveAnimals());
            dispatch(clearFeedCurves());
            dispatch(clearActiveLocations()); 
            ifSuccess(farm.domain)
          }
        }}
        buttonType={ButtonTypes.textButton}
        startEnhancer={<glyphs.ArrowSmooth fillColor={colors.greyDarkPrimary}/>}
        overrides={{
          contentStyles: {color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph},
          startEnhancerStyles: {marginRight: "15px"}
        }}
      />,
      id: farm.company_id,
      farmName: farm.name,
      farmDomain: farm.domain,
    })) || []
  ), [dispatch, ifSuccess, userFarms, userToken]);
  
  
  const accessorData = useMemo<GenericDataParameters[]>(() => ([
    {headerValue: `${t("loginFlow.chooseSite.siteName", {defaultValue: "site name"})}`, accessor: "action", type: ListData_ValueTypes.Button, staticColumn: true, sort: true, filter: true, width: "180px"},
    {headerValue: `${t("loginFlow.chooseSite.domain", {defaultValue: "domain"})}`, accessor: "farmDomain", type: ListData_ValueTypes.String, sort: true, filter: true},
    // {headerValue: `${t("loginFlow.chooseSite.id", {defaultValue: "id"})}`, accessor: "id", type: ListData_ValueTypes.Number, sort: true, filter: true},
  ]),[t]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.blueTable,
    canEdit: false
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: globalFilter,
    state: {
      globalFilter: globalFilterValue,
      columnPinning: {
        left: ["action"]
      }
    },
    onGlobalFilterChange: setGlobalFilterValue,
  })

  const reactiveTable = ReactiveTable({table, tableType: TableStyles_Types.blueTable, emptyText: t("loginFlow.login.noSitesFound", {defaultValue: "no sites found"}), renderFooter: false, shouldAlwaysRerender: true})

  return (
    <frames.DecorBg>
      <NewModal
        modalState
        header={t("loginFlow.chooseSite.chooseSite", {defaultValue: "choose site"})}
        overrides={{
          overlayColor: colors.blackPrimary + "99",
          // mainContentScroll: true,
        }}
        topRightContent={
          <NewButton content={t("loginFlow.login.logOut", {defaultValue: "log out"})}
            startEnhancer={glyphs.Arrow({rotate: 180})}
            onClick={() => doLogOut()}
            buttonType={ButtonTypes.textButton}
          />
        }
        topLeftContent={ <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "end", gap: " 0px 10px"}}>
          <NewButton content={t("user.userInformation", {defaultValue: "user information"})}
            startEnhancer={glyphs.User({})}
            onClick={() => {RootNavigation({ Navigator: navigator, navigation_page: Pages.userInformations })} }
            buttonType={ButtonTypes.textButton}
            overrides={{
              contentStyles: {whiteSpace: "nowrap"}
            }}
          />
          {adminInfo && (
            <NewButton content={t("loginFlow.chooseSite.tools", {defaultValue: "tools"})}
              startEnhancer={<glyphs.Setting/>}
              buttonType={ButtonTypes.textButton}
              onClick={() => {RootNavigation({ Navigator: navigator, navigation_page: Pages.log })} }
            />
          )}
        </div>}
        headerContent={
        <NewInput
          ref={searchRef}
          onChange={(value: string) => {
            setSearchTerm(value);
            setGlobalFilterValue(value);
            console.log("Search Term:", value); 
          }}
          placeholder={t("search", { defaultValue: "search" })}
          startEnhancer={glyphs.Search({})}
        />
        }
        mainContent={
          <div style={{width: "100%", minWidth: "400px"}}>{reactiveTable}</div>
        }
      />
    </frames.DecorBg>
  )
}